import type { Operation, Operations } from './api.generated';
import type { Command, ScreenId, WorkspaceProps } from './model';

export const readPermissions: Partial<Record<ScreenId, string[]>> = {
  '04':['DASHBOARD_READ'],'05':['MEMBER_READ'],'06':['MEMBER_READ'],
  '07':['TRANSACTION_READ'],'08':['TRANSACTION_READ'],'09':['TRANSFER_READ'],'10':['TRANSFER_READ'],
  '11':['PAYMENT_CANCEL','TRANSFER_EXECUTE'],'12':['BENEFIT_READ'],'13':['INQUIRY_MANAGE'],
  '14':['NOTIFICATION_MANAGE'],'15':['CONTENT_MANAGE'],'16':['PARTNER_MANAGE'],'17':['PROVIDER_MANAGE'],
  '18':['AUDIT_READ','PROVIDER_MANAGE'],'19':['AUDIT_READ'],'20':['DATA_EXPORT'],'21':['ADMIN_MANAGE'],'22':['ADMIN_MANAGE'],
};
export const writePermissions: Partial<Record<ScreenId,string>> = {
  '14':'NOTIFICATION_MANAGE','15':'CONTENT_MANAGE','16':'PARTNER_MANAGE','17':'PROVIDER_MANAGE',
  '18':'PROVIDER_MANAGE','21':'ADMIN_MANAGE','22':'ADMIN_MANAGE',
};

export function request<K extends Operation>(
  send: WorkspaceProps['onCommand'],
  command: { operation: K; body: Operations[K]['body']; query?: Operations[K]['query']; params?: Record<string, string | number> },
): Promise<Operations[K]['response']> {
  return send(command as Command) as Promise<Operations[K]['response']>;
}

export function allowed(props: Pick<WorkspaceProps, 'actor' | 'readOnly' | 'state'>, permission: string) {
  return !props.readOnly && !['loading', 'error', 'denied', 'expired', 'not-found'].includes(props.state ?? 'ready')
    && props.actor.authorities.includes(permission);
}

export type FailureRecovery = "input" | "login" | "permission" | "refresh" | "new-file" | "different-admin" | "reconcile";

export interface FailureFeedback {
  readonly message: string;
  readonly reconcile: boolean;
  readonly recovery: FailureRecovery;
}

const businessFailures: Readonly<Record<string, Omit<FailureFeedback, "message"> & { readonly message: string }>> = {
  NOTIFICATION_TEMPLATE_CONFIGURATION_REQUIRED: {
    message: "연동 설정의 활성 비즈뿌리오 설정에 KAPI 계정 ID·인증키와 발신 프로필 키를 등록해 주세요.",
    reconcile: false,
    recovery: "input",
  },
  NOTIFICATION_TEMPLATE_PROVIDER_UNAVAILABLE: {
    message: "비즈뿌리오 템플릿을 조회하지 못했습니다. KAPI 인증 정보와 조회 권한을 확인한 뒤 다시 조회해 주세요.",
    reconcile: false,
    recovery: "refresh",
  },
  TRANSACTION_REVIEW_ALREADY_CLAIMED: {
    message: "다른 관리자가 먼저 심사를 선점했습니다. 최신 담당자와 잠금 상태를 확인해 주세요.",
    reconcile: true,
    recovery: "refresh",
  },
  TRANSACTION_REVIEW_STATE_INVALID: {
    message: "거래 심사 상태가 변경되었습니다. 최신 상태를 조회한 뒤 계속해 주세요.",
    reconcile: true,
    recovery: "refresh",
  },
  TRANSFER_ALREADY_PROCESSING: {
    message: "이미 처리 중이거나 완료된 송금입니다. 다시 실행하지 말고 최신 상태를 조회해 주세요.",
    reconcile: true,
    recovery: "refresh",
  },
  TRANSFER_NOT_DUE: {
    message: "예약 송금 예정시각 전에는 실행할 수 없습니다. 예정시각 이후 자동 처리 결과를 확인해 주세요.",
    reconcile: false,
    recovery: "input",
  },
  REFUND_RESULT_UNKNOWN: {
    message: "환불 결과를 확인 중입니다. 다시 실행하지 말고 최신 상태와 공급자 결과를 확인해 주세요.",
    reconcile: true,
    recovery: "reconcile",
  },
  REFUND_EXECUTION_FAILED: {
    message: "환불 실행이 실패 상태로 기록되었습니다. 최신 상태와 실패 사유를 확인해 주세요.",
    reconcile: true,
    recovery: "refresh",
  },
  BENEFIT_SELF_APPROVAL_DENIED: {
    message: "기안한 관리자는 직접 승인할 수 없습니다. 다른 관리자에게 승인을 요청해 주세요.",
    reconcile: true,
    recovery: "different-admin",
  },
  BENEFIT_STATUS_INVALID: {
    message: "혜택지원 처리 상태가 변경되었습니다. 최신 상태를 조회한 뒤 계속해 주세요.",
    reconcile: true,
    recovery: "refresh",
  },
  BENEFIT_APPROVAL_NOT_FOUND: {
    message: "처리할 승인 요청이 이미 변경되었습니다. 최신 승인 이력을 조회해 주세요.",
    reconcile: true,
    recovery: "refresh",
  },
  BENEFIT_DOCUMENT_INVALID: {
    message: "지급 증빙을 사용할 수 없습니다. 이 관리자 계정으로 새 지급 증빙을 등록해 주세요.",
    reconcile: false,
    recovery: "new-file",
  },
  INQUIRY_ASSIGNMENT_INVALID: {
    message: "문의 담당자가 이미 변경되었습니다. 최신 담당자와 문의 상태를 조회해 주세요.",
    reconcile: true,
    recovery: "refresh",
  },
  INQUIRY_REPLY_NOT_ALLOWED: {
    message: "현재 담당자나 문의 상태로 답변을 등록할 수 없습니다. 최신 상태를 조회해 주세요.",
    reconcile: true,
    recovery: "refresh",
  },
  NOTIFICATION_RETRY_NOT_ALLOWED: {
    message: "알림 상태가 변경되어 재시도할 수 없습니다. 최신 발송 상태와 시도 이력을 조회해 주세요.",
    reconcile: true,
    recovery: "refresh",
  },
};

export function failure(error: unknown): FailureFeedback {
  const detail = error as { status?: number; kind?: string; code?: string; correlationId?: string } | null;
  const status = detail?.status;
  const businessFailure = detail?.code ? businessFailures[detail.code] : undefined;
  const fallback: FailureFeedback = status === 401 ? { message: '로그인이 만료되었습니다. 다시 로그인해 주세요.', reconcile: true, recovery: 'login' }
    : status === 403 ? { message: '처리 권한이 없습니다. 권한과 최신 상태를 확인해 주세요.', reconcile: true, recovery: 'permission' }
    : status === 409 ? { message: '다른 작업으로 상태가 변경되었습니다. 최신 상태를 조회해 주세요.', reconcile: true, recovery: 'refresh' }
    : status === 410 ? { message: '파일이나 요청의 유효기간이 끝났습니다. 다시 요청해 주세요.', reconcile: false, recovery: 'new-file' }
    : status === 404 ? { message: '대상을 찾을 수 없습니다. 목록을 다시 조회해 주세요.', reconcile: true, recovery: 'refresh' }
    : status === 400 ? { message: '입력값과 처리 조건을 확인해 주세요.', reconcile: false, recovery: 'input' }
    : { message: '처리 결과를 확인하지 못했습니다. 다시 전송하기 전에 최신 상태를 조회해 주세요.', reconcile: true, recovery: 'reconcile' };
  const feedback = businessFailure ?? fallback;
  const message = feedback.message;
  return {
    ...feedback,
    message: detail?.correlationId ? `${message} 문의 코드: ${detail.correlationId}` : message,
  };
}

export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
